import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { getAssignmentQuestionUrl, getAssignmentEndUrl } from '../../utils';
import {
  useCreateStudentAssignment,
  useGetAssignment,
  useStartStudentAssignment
} from '../../queries';
import { ASSIGNMENT_END_REASON, STUDENTASSIGNMENT_STATUS } from '../../constants';
import { useStartScreen, useAuth, useCurrentTime } from '../../hooks';
import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import { FullPageLoader, ConfirmationModal } from '../shared';
import AccessDenied from '../AccessDenied';
import AssignmentWelcomeScreen from './AssignmentWelcomeScreen';
import { getQuestionsToAnswerCount } from '../../utils/choiceGroups';

const AssignmentStartScreen = (props) => {
  const { assignmentId } = props;
  const { data: assignmentData, isLoading, isError, error } = useGetAssignment(assignmentId);
  const [studentAssignment, setStudentAssignment] = React.useState(null);
  const [isStrictModeModalOpen, setIsStrictModeModalOpen] = React.useState(false);
  const { user, signOut } = useAuth();
  const currentTime = useCurrentTime();

  const {
    mutate: createStudentAssignment,
    isCreateLoading,
    data: createData
  } = useCreateStudentAssignment(assignmentId);
  const { bannerMessage, allowStart, isAssignmentCompleted, studentAssignmentId } = useStartScreen({
    assignmentData,
    createData: createData?.data,
    currentTime
  });

  const { mutate: startStudentAssignment, isStartLoading } = useStartStudentAssignment(
    studentAssignment?._id
  );

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isCreateLoading && !createData) {
      createStudentAssignment(null, {
        onSuccess: ({ data }) => {
          setStudentAssignment(data);
        }
      });
    }
  }, [isCreateLoading, createData, createStudentAssignment]);

  useEffect(() => {
    if (isAssignmentCompleted) {
      navigate(
        getAssignmentEndUrl({
          studentAssignmentId,
          reason: ASSIGNMENT_END_REASON.ALREADY_COMPLETED
        })
      );
    }
  }, [isAssignmentCompleted, navigate, studentAssignmentId]);

  if (isError && error?.response?.status === 403) {
    console.error('Access Denied');
    return (
      <LoggedInUserWrapper welcomeMode>
        <AccessDenied reason={error?.response?.data?.code} />
      </LoggedInUserWrapper>
    );
  }

  if (
    isLoading ||
    !assignmentData ||
    isAssignmentCompleted ||
    (assignmentData?.startDate && !currentTime)
  ) {
    return <FullPageLoader />;
  }

  const {
    duration,
    name,
    welcomeScreen: { elements },
    startButtonText,
    settings: { enableStrictMode }
  } = assignmentData;

  const durationMinutes = duration ? Math.floor(duration / (1000 * 60)) : null;
  // What the student has to answer: an X of Y rule counts its required answers, not every
  // alternative, and a passage counts through its questions.
  const totalQuestions = getQuestionsToAnswerCount(assignmentData);

  const startText =
    isCreateLoading || isStartLoading || !studentAssignment
      ? 'Loading'
      : studentAssignment?.status === STUDENTASSIGNMENT_STATUS.YET_TO_START
      ? startButtonText
      : 'Resume my assignment';

  const isStartButtonLoading = isCreateLoading || isStartLoading || !studentAssignment;
  const isStartButtonDisabled = isCreateLoading || isStartLoading || !allowStart;

  const handleStart = () => {
    if (enableStrictMode) {
      setIsStrictModeModalOpen(true);
    } else {
      startAssignment();
    }
  };

  const startAssignment = () => {
    if (studentAssignment?.status === STUDENTASSIGNMENT_STATUS.YET_TO_START) {
      startStudentAssignment();
    } else {
      const questionId = studentAssignment?.studentResponse[0]?.questionId;
      navigate(
        getAssignmentQuestionUrl({
          studentAssignmentId: studentAssignment._id,
          questionId: questionId
        })
      );
    }
  };

  const onConfirmStrictMode = () => {
    setIsStrictModeModalOpen(false);
    startAssignment();
  };

  return (
    <LoggedInUserWrapper
      welcomeMode
      bannerProps={{ message: bannerMessage }}
      enableStrictMode={enableStrictMode}
    >
      <AssignmentWelcomeScreen
        assignmentName={name}
        userName={user?.displayName}
        userEmail={user?.email}
        totalQuestions={totalQuestions}
        durationMinutes={durationMinutes}
        enableStrictMode={enableStrictMode}
        instructions={elements}
        startButtonText={startText}
        isLoading={isStartButtonLoading}
        isDisabled={isStartButtonDisabled}
        onStart={handleStart}
        onLogout={signOut}
      />
      <ConfirmationModal
        headerText="⚠️ Strict Mode Enabled"
        isOpen={isStrictModeModalOpen}
        isClosable={false}
        primaryButtonText="Confirm"
        modalText="This assignment is in strict mode. Leaving the session or opening a new browser window will submit your progress automatically."
        onAction={onConfirmStrictMode}
      />
    </LoggedInUserWrapper>
  );
};

export default AssignmentStartScreen;
